/**
 * ============================================================================
 * USE ANALYTICS HOOK
 * ============================================================================
 * 
 * Custom hook for analytics and reporting
 */

import { useState, useCallback } from 'react';
import * as analyticsService from '../services/analytics';

export const useAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);
  const [transactionAnalytics, setTransactionAnalytics] = useState(null);
  const [walletAnalytics, setWalletAnalytics] = useState(null);
  const [disputeAnalytics, setDisputeAnalytics] = useState(null);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [userGrowthMetrics, setUserGrowthMetrics] = useState(null);
  const [realtimeMetrics, setRealtimeMetrics] = useState(null);
  const [comparisonAnalytics, setComparisonAnalytics] = useState(null);

  /**
   * Get dashboard analytics
   */
  const loadDashboardAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getDashboardAnalytics(filters);
      const data = response?.data ?? response;
      setDashboardAnalytics(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load dashboard analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get transaction analytics
   */
  const loadTransactionAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getTransactionAnalytics(filters);
      setTransactionAnalytics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load transaction analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get wallet analytics
   */
  const loadWalletAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getWalletAnalytics(filters);
      setWalletAnalytics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load wallet analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get dispute analytics
   */
  const loadDisputeAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getDisputeAnalytics(filters);
      setDisputeAnalytics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load dispute analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get payment analytics
   */
  const loadPaymentAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getPaymentAnalytics(filters);
      setPaymentAnalytics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load payment analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get revenue analytics
   */
  const loadRevenueAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getRevenueAnalytics(filters);
      setRevenueAnalytics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load revenue analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get user growth metrics
   */
  const loadUserGrowthMetrics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getUserGrowthMetrics(filters);
      const data = response?.data ?? response;
      setUserGrowthMetrics(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load user growth metrics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get realtime metrics
   */
  const loadRealtimeMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getRealtimeMetrics();
      const data = response?.data ?? response;
      setRealtimeMetrics(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load realtime metrics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Export analytics data
   */
  const exportAnalyticsData = useCallback(async (type, options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.exportAnalytics({
        type,
        ...options,
      });
      
      // Handle file download
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      } else if (response instanceof Blob) {
        // Handle blob response
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        const format = options.format || 'json';
        link.download = `analytics-${type}-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else if (response.data) {
        // Create blob and download
        const format = options.format || 'json';
        const mimeTypes = {
          csv: 'text/csv',
          json: 'application/json',
          xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        };
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: mimeTypes[format] || 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics-${type}-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      
      return response.data || response;
    } catch (err) {
      setError(err.message || 'Failed to export analytics data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get comparison analytics
   */
  const loadComparisonAnalytics = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getComparisonAnalytics(filters);
      setComparisonAnalytics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load comparison analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get custom analytics
   */
  const loadCustomAnalytics = useCallback(async (endpoint, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsService.getCustomAnalytics(endpoint, filters);
      return response?.data ?? response;
    } catch (err) {
      setError(err.message || 'Failed to load custom analytics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    dashboardAnalytics,
    transactionAnalytics,
    walletAnalytics,
    disputeAnalytics,
    paymentAnalytics,
    revenueAnalytics,
    userGrowthMetrics,
    realtimeMetrics,
    comparisonAnalytics,
    loading,
    error,
    
    // Actions
    loadDashboardAnalytics,
    loadTransactionAnalytics,
    loadWalletAnalytics,
    loadDisputeAnalytics,
    loadPaymentAnalytics,
    loadRevenueAnalytics,
    loadUserGrowthMetrics,
    loadRealtimeMetrics,
    exportAnalyticsData,
    loadComparisonAnalytics,
    loadCustomAnalytics,
  };
};

export default useAnalytics;
